/**
 * Sessions Report Store
 * Zustand store for sessions report state management
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { servicesContainer } from '@/core/di/services-container';
import {
  SessionGridRow,
  SessionDetail,
  SessionFilters,
  getDefaultFilters,
} from '../types/session-types';
import { MeterValueGridRow } from '../types/meter-value-types';
import { TariffLevelMapping } from '../types/api-types';
import { ColumnGroup, columnGroups } from '../components/SessionsGridColumns';

interface SessionsReportState {
  // Data
  sessions: SessionGridRow[];
  selectedSession: SessionGridRow | null;
  selectedSessionDetail: SessionDetail | null;
  totalCount: number;

  // Tariff Levels (for dynamic column headers matching Flutter)
  tariffLevels: TariffLevelMapping[];
  isLoadingTariffLevels: boolean;

  // Pagination
  currentPage: number;
  pageSize: number;
  pageSizeOptions: number[];

  // Loading states
  isLoading: boolean;
  isLoadingDetail: boolean;

  // Errors
  error: string | null;
  detailError: string | null;

  // Filters
  filters: SessionFilters;

  // UI state
  isFilterPanelOpen: boolean;
  isSearchOpen: boolean;
  showAdvancedFilters: boolean;

  // Column visibility state (HIVE-6245 #11: persisted)
  enabledColumnGroups: ColumnGroup[];

  // Export state (HIVE-6245 #14)
  isExportingSessions: boolean;
  exportProgress: { current: number; total: number } | null;

  // Meter Values state
  meterValues: MeterValueGridRow[];
  meterValuesCount: number;
  meterValuesPage: number;
  meterValuesPageSize: number;
  meterValuesPageSizeOptions: number[];
  isLoadingMeterValues: boolean;
  meterValuesError: string | null;
  isMeterValueBladeOpen: boolean;
  isExportingMeterValues: boolean;
  filterEmptyDelta: boolean; // Hide rows with Delta Energy = 0

  // Actions
  loadSessions: () => Promise<void>;
  loadPage: (page: number) => Promise<void>;
  setPageSize: (size: number) => void;
  loadSessionDetail: (session: SessionGridRow) => Promise<void>;
  setSelectedSession: (session: SessionGridRow | null) => void;
  clearSelectedSession: () => void;
  setFilters: (filters: Partial<SessionFilters>) => void;
  setDateRange: (dateFrom: string, dateTo: string) => void;
  toggleGroupByLocation: () => void;
  toggleFilterPanel: () => void;
  toggleSearch: () => void;
  toggleAdvancedFilters: () => void;
  viewReport: () => Promise<void>;
  reset: () => void;
  setColumnGroupEnabled: (groupId: ColumnGroup, enabled: boolean) => void;
  loadTariffLevels: () => Promise<void>;

  // Sessions Export action (HIVE-6245 #14, HIVE-6234: column filtering)
  exportSessions: (selectedColumns?: string[]) => Promise<void>;

  // Meter Values actions
  openMeterValuesBlade: () => void;
  closeMeterValuesBlade: () => void;
  loadMeterValues: () => Promise<void>;
  loadMeterValuesPage: (page: number) => Promise<void>;
  setMeterValuesPageSize: (size: number) => void;
  setFilterEmptyDelta: (value: boolean) => void;
  exportMeterValues: () => Promise<void>;
}

const initialState = {
  sessions: [],
  selectedSession: null,
  selectedSessionDetail: null,
  totalCount: 0,
  tariffLevels: [] as TariffLevelMapping[],
  isLoadingTariffLevels: false,
  currentPage: 0,
  pageSize: 100,
  pageSizeOptions: [10, 25, 50, 100, 200, 500, 1000],
  isLoading: false,
  isLoadingDetail: false,
  error: null,
  detailError: null,
  filters: getDefaultFilters(),
  isFilterPanelOpen: false,
  isSearchOpen: false,
  showAdvancedFilters: false,
  // Column visibility state (HIVE-6245 #11)
  enabledColumnGroups: columnGroups.filter((g) => g.defaultEnabled).map((g) => g.id),
  // Export state (HIVE-6245 #14)
  isExportingSessions: false,
  exportProgress: null,
  // Meter Values initial state
  meterValues: [] as MeterValueGridRow[],
  meterValuesCount: 0,
  meterValuesPage: 0,
  meterValuesPageSize: 500,
  meterValuesPageSizeOptions: [100, 200, 500, 1000],
  isLoadingMeterValues: false,
  meterValuesError: null,
  isMeterValueBladeOpen: false,
  isExportingMeterValues: false,
  filterEmptyDelta: true, // Default: hide rows with Delta Energy = 0
};

export const useSessionsReportStore = create<SessionsReportState>()(
  persist(
    (set, get) => ({
      ...initialState,

      /**
       * Load sessions with current filters and pagination
       */
      loadSessions: async () => {
        const { filters, currentPage, pageSize } = get();

        set({ isLoading: true, error: null });

        try {
          const services = servicesContainer.getServices();
          const result = await services.getSessionsUseCase.loadPage(
            filters,
            currentPage,
            pageSize
          );

          set({
            sessions: result.sessions,
            totalCount: result.totalCount,
            isLoading: false,
          });
        } catch (error) {
          console.error('Failed to load sessions:', error);
          set({
            error:
              error instanceof Error
                ? error.message
                : 'Failed to load sessions',
            isLoading: false,
          });
        }
      },

      /**
       * Load a specific page
       */
      loadPage: async (page: number) => {
        const { filters, pageSize } = get();

        set({ isLoading: true, error: null, currentPage: page });

        try {
          const services = servicesContainer.getServices();
          const result = await services.getSessionsUseCase.loadPage(
            filters,
            page,
            pageSize
          );

          set({
            sessions: result.sessions,
            totalCount: result.totalCount,
            isLoading: false,
          });
        } catch (error) {
          console.error('Failed to load sessions:', error);
          set({
            error:
              error instanceof Error
                ? error.message
                : 'Failed to load sessions',
            isLoading: false,
          });
        }
      },

      /**
       * Change page size and reload
       */
      setPageSize: (size: number) => {
        set({ pageSize: size, currentPage: 0 });
        get().loadSessions();
      },

      /**
       * Load detail for a specific session
       */
      loadSessionDetail: async (session: SessionGridRow) => {
        // Clear previous session's meter values when loading a new session
        set({
          isLoadingDetail: true,
          detailError: null,
          selectedSession: session,
          // Reset meter values for the new session
          meterValues: [],
          meterValuesCount: 0,
          meterValuesPage: 0,
          meterValuesError: null,
        });

        try {
          const services = servicesContainer.getServices();
          const result = await services.getSessionsUseCase.getSessionDetail(
            session
          );

          set({
            selectedSessionDetail: result.detail,
            isLoadingDetail: false,
          });
        } catch (error) {
          console.error('Failed to load session detail:', error);
          set({
            detailError:
              error instanceof Error
                ? error.message
                : 'Failed to load session details',
            isLoadingDetail: false,
          });
        }
      },

      /**
       * Set selected session
       */
      setSelectedSession: (session: SessionGridRow | null) => {
        set({
          selectedSession: session,
          selectedSessionDetail: null,
          detailError: null,
        });
      },

      /**
       * Clear selection
       */
      clearSelectedSession: () => {
        set({
          selectedSession: null,
          selectedSessionDetail: null,
          detailError: null,
          // Also clear meter values when session is deselected
          meterValues: [],
          meterValuesCount: 0,
          meterValuesPage: 0,
          meterValuesError: null,
        });
      },

      /**
       * Update filters (resets to page 0)
       */
      setFilters: (newFilters: Partial<SessionFilters>) => {
        const { filters } = get();
        set({
          filters: { ...filters, ...newFilters },
          currentPage: 0,
        });
      },

      /**
       * Set date range
       */
      setDateRange: (dateFrom: string, dateTo: string) => {
        const { filters } = get();
        set({
          filters: { ...filters, dateFrom, dateTo },
          currentPage: 0,
        });
      },

      /**
       * Toggle group by location
       */
      toggleGroupByLocation: () => {
        const { filters } = get();
        set({
          filters: { ...filters, groupByLocation: !filters.groupByLocation },
        });
      },

      /**
       * Toggle filter panel
       */
      toggleFilterPanel: () => {
        const { isFilterPanelOpen } = get();
        set({ isFilterPanelOpen: !isFilterPanelOpen });
      },

      /**
       * Toggle search
       */
      toggleSearch: () => {
        const { isSearchOpen } = get();
        set({ isSearchOpen: !isSearchOpen });
      },

      /**
       * Toggle advanced filters visibility
       */
      toggleAdvancedFilters: () => {
        const { showAdvancedFilters } = get();
        set({ showAdvancedFilters: !showAdvancedFilters });
      },

      /**
       * View report - reload with current filters (resets to page 0)
       */
      viewReport: async () => {
        set({ currentPage: 0 });
        await get().loadSessions();
      },

      /**
       * Reset store to initial state
       */
      reset: () => {
        set(initialState);
      },

      /**
       * Set column group enabled state (HIVE-6245 #11: persisted, HIVE-6300: cascading)
       *
       * Cascading logic (matches Flutter):
       * - Disabling paymentInfo → also disables buyInfo
       * - Disabling cost (Fees) → also disables vatInclude
       */
      setColumnGroupEnabled: (groupId: ColumnGroup, enabled: boolean) => {
        const { enabledColumnGroups } = get();

        let newGroups: ColumnGroup[];

        if (enabled) {
          // Simply add the group
          newGroups = [...enabledColumnGroups, groupId];
        } else {
          // Remove the group and any dependent groups
          newGroups = enabledColumnGroups.filter((id) => id !== groupId);

          // Cascading disable: if disabling paymentInfo, also disable buyInfo
          if (groupId === 'paymentInfo') {
            newGroups = newGroups.filter((id) => id !== 'buyInfo');
          }

          // Cascading disable: if disabling cost (Fees), also disable vatInclude
          if (groupId === 'cost') {
            newGroups = newGroups.filter((id) => id !== 'vatInclude');
          }
        }

        set({ enabledColumnGroups: newGroups });
      },

      /**
       * Load tariff levels mapping for current operator
       * Fetches tariff period names for dynamic column headers
       */
      loadTariffLevels: async () => {
        set({ isLoadingTariffLevels: true });
        try {
          const services = servicesContainer.getServices();
          const response = await services.getTariffLevelsUseCase.execute();
          set({
            tariffLevels: response.rows || [],
            isLoadingTariffLevels: false
          });
        } catch (error) {
          console.error('Failed to load tariff levels:', error);
          set({
            tariffLevels: [],
            isLoadingTariffLevels: false
          });
        }
      },

      /**
       * Export all sessions matching current filters to Excel (HIVE-6245 #14, HIVE-6234)
       * Uses client-side batched fetch to get all pages
       * @param selectedColumns - Optional array of column names to export (if not provided, exports all)
       */
      exportSessions: async (selectedColumns?: string[]) => {
        const { filters, totalCount } = get();

        if (totalCount === 0) {
          return;
        }

        set({ isExportingSessions: true, exportProgress: { current: 0, total: totalCount } });

        try {
          const services = servicesContainer.getServices();
          const allSessions: SessionGridRow[] = [];
          const batchSize = 500;
          let page = 0;

          // Fetch all pages
          while (allSessions.length < totalCount) {
            const result = await services.getSessionsUseCase.loadPage(
              filters,
              page,
              batchSize
            );
            allSessions.push(...result.sessions);
            set({ exportProgress: { current: allSessions.length, total: totalCount } });
            page++;

            // Safety break to prevent infinite loop
            if (result.sessions.length === 0) break;
          }

          // Transform sessions to export format (ALL columns matching UI order)
          // Use column IDs as keys for proper filtering
          const allColumnsData = allSessions.map((session) => ({
            // 1. Basic session info (always visible)
            sessionId: session.sessionId,
            startTime: session.startTime ? format(new Date(session.startTime), 'yyyy-MM-dd HH:mm:ss') : '',
            stopTime: session.stopTime ? format(new Date(session.stopTime), 'yyyy-MM-dd HH:mm:ss') : '',
            durationInSeconds: session.durationInSeconds ?? '',

            // 2. Session Info group - Energy measurements
            meterStart: session.meterStart ?? '',
            meterStop: session.meterStop ?? '',
            totalEnergy: session.totalEnergy?.toFixed(2) ?? '',
            stopReason: session.stopReason ?? '',

            // 3. Charge Point group - Location/CP info
            locationName: session.locationName ?? '',
            chargePointNumber: session.chargePointNumber ?? '',
            connectorId: session.connectorId ?? '',
            location: session.kioskActorHierarchy ?? '',
            locationId: session.locationId ?? '',
            machineUsage: session.machineUsage ?? '',
            physicalReference: session.physicalReference ?? '',
            chargePointId: session.chargePointId ?? '',
            startSource: session.startSource ?? '',

            // 4. PTP Info group - OCPI/Device info
            isOcpi: session.isOcpi ? 'Yes' : 'No',
            ocpiEmspName: session.ocpiEmspName ?? '',
            prepaidCard: session.prepaidCard ?? '',
            deviceSerial: session.deviceSerial ?? '',
            machineModelName: session.machineModelName ?? '',
            sparkMachineId: session.sparkMachineId ?? '',

            // 5. Account group
            accountName: session.accountName ?? '',
            accountId: session.accountId ?? '',
            accountExternalCode: session.accountExternalCode ?? '',
            isExternalPayment: session.isExternalPayment ? 'Yes' : 'No',
            tokenId: session.tokenId ?? '',
            tokenDisplayNumber: session.tokenDisplayNumber ?? '',
            cardType: session.cardType ?? '',
            rfid: session.rfid ?? '',

            // 6. Tariff Levels (L1-L9) - positioned BEFORE fee columns (matching UI)
            l1UnitCost: session.l1UnitCost?.toFixed(2) ?? '',
            l1Energy: session.l1Energy?.toFixed(2) ?? '',
            l1Cost: session.l1Cost?.toFixed(2) ?? '',
            l2UnitCost: session.l2UnitCost?.toFixed(2) ?? '',
            l2Energy: session.l2Energy?.toFixed(2) ?? '',
            l2Cost: session.l2Cost?.toFixed(2) ?? '',
            l3UnitCost: session.l3UnitCost?.toFixed(2) ?? '',
            l3Energy: session.l3Energy?.toFixed(2) ?? '',
            l3Cost: session.l3Cost?.toFixed(2) ?? '',
            l4UnitCost: session.l4UnitCost?.toFixed(2) ?? '',
            l4Energy: session.l4Energy?.toFixed(2) ?? '',
            l4Cost: session.l4Cost?.toFixed(2) ?? '',
            l5UnitCost: session.l5UnitCost?.toFixed(2) ?? '',
            l5Energy: session.l5Energy?.toFixed(2) ?? '',
            l5Cost: session.l5Cost?.toFixed(2) ?? '',
            l6UnitCost: session.l6UnitCost?.toFixed(2) ?? '',
            l6Energy: session.l6Energy?.toFixed(2) ?? '',
            l6Cost: session.l6Cost?.toFixed(2) ?? '',
            l7UnitCost: session.l7UnitCost?.toFixed(2) ?? '',
            l7Energy: session.l7Energy?.toFixed(2) ?? '',
            l7Cost: session.l7Cost?.toFixed(2) ?? '',
            l8UnitCost: session.l8UnitCost?.toFixed(2) ?? '',
            l8Energy: session.l8Energy?.toFixed(2) ?? '',
            l8Cost: session.l8Cost?.toFixed(2) ?? '',
            l9UnitCost: session.l9UnitCost?.toFixed(2) ?? '',
            l9Energy: session.l9Energy?.toFixed(2) ?? '',
            l9Cost: session.l9Cost?.toFixed(2) ?? '',

            // 7. Cost group - Energy Fee (use column IDs but map to actual fields)
            energyCostExcVat: session.cost?.toFixed(2) ?? '',
            energyVat: session.vatCost?.toFixed(2) ?? '',
            energyCostIncVat: ((session.cost ?? 0) + (session.vatCost ?? 0))?.toFixed(2) ?? '',

            // 8. Cost group - Time Fee
            timeFeeWithoutVat: session.timeFeeWithoutVat?.toFixed(2) ?? '',
            timeFeeVat: session.timeFeeVat?.toFixed(2) ?? '',
            timeFee: session.timeFee?.toFixed(2) ?? '',

            // 9. Cost group - Connection Fee (Flat Fee)
            connectionFeeWithoutVat: session.connectionFeeWithoutVat?.toFixed(2) ?? '',
            connectionFeeVat: session.connectionFeeVat?.toFixed(2) ?? '',
            connectionFee: session.connectionFee?.toFixed(2) ?? '',

            // 10. Cost group - Idle Fee
            idleFeeWithoutVat: session.idleFeeWithoutVat?.toFixed(2) ?? '',
            idleFeeVat: session.idleFeeVat?.toFixed(2) ?? '',
            idleFee: session.idleFee?.toFixed(2) ?? '',

            // 11. Payment Info group - Total Fees (before discount)
            totalPaymentCostExcVat: session.totalPaymentCostExcVat?.toFixed(2) ?? '',
            totalPaymentVat: session.totalPaymentVat?.toFixed(2) ?? '',
            totalPaymentCostIncVat: session.totalPaymentCostIncVat?.toFixed(2) ?? '',

            // 12. Payment Info group - Settlement (use column IDs but map to actual fields)
            settlementExcVat: session.totalPaymentAfterDiscountExcVat?.toFixed(2) ?? '',
            settlementVat: session.totalPaymentAfterDiscountVat?.toFixed(2) ?? '',
            settlementIncVat: session.settlementValue?.toFixed(2) ?? '',

            // 13. Payment Info group - Discount
            communityName: session.communityName ?? '',
            communityValue: session.communityValue ?? '',
            discountExcVat: session.discountExcVat?.toFixed(2) ?? '',
            discountVat: session.discountVat?.toFixed(2) ?? '',
            discountIncVat: session.discountIncVat?.toFixed(2) ?? '',

            // 14. Payment Info group - Total Cost (after discount)
            totalPaymentAfterDiscountExcVat: session.totalPaymentAfterDiscountExcVat?.toFixed(2) ?? '',
            totalPaymentAfterDiscountVat: session.totalPaymentAfterDiscountVat?.toFixed(2) ?? '',
            totalPaymentAfterDiscountIncVat: session.totalPaymentAfterDiscountIncVat?.toFixed(2) ?? '',

            // 15. Payment Info group - Payment ID
            paymentId: session.nayaxTransactionId ?? '',

            // 16. Buy Info group (operator cost/profit)
            utilityEnergyCost: session.energyBuyCost?.toFixed(2) ?? '',
            energyIncome: session.energyProfit?.toFixed(2) ?? '',
            totalIncome: session.totalProfit?.toFixed(2) ?? '',

            // 17. Other fields (not currently in UI columns but in SessionGridRow)
            evTierPricing: session.evTierPricing ?? '',
            vatRate: session.vatRate ?? '',
            currencyName: session.currencyName ?? '',
            isVirtualSession: session.isVirtualSession ? 'Yes' : 'No',
            nayaxTransactionId: session.nayaxTransactionId ?? '',
          }));

          // Column header mapping (column ID → display name)
          const columnHeaders: Record<string, string> = {
            sessionId: 'Session ID',
            startTime: 'Started At',
            stopTime: 'Ended At',
            durationInSeconds: 'Duration (seconds)',
            meterStart: 'Meter Start [kWh]',
            meterStop: 'Meter Stop [kWh]',
            totalEnergy: 'Energy [kWh]',
            stopReason: 'Stop Reason',
            locationName: 'Charging Station Name',
            chargePointNumber: 'Charging Station Number',
            connectorId: 'Connector ID',
            location: 'Location',
            locationId: 'Location ID',
            machineUsage: 'Usage Type',
            physicalReference: 'Physical Ref',
            chargePointId: 'CP ID',
            startSource: 'Start Source',
            isOcpi: 'OCPI',
            ocpiEmspName: 'EMSP',
            prepaidCard: 'Prepaid Card',
            deviceSerial: 'Device Serial',
            machineModelName: 'Device Type',
            sparkMachineId: 'Spark Machine ID',
            accountName: 'Account Name',
            accountId: 'Account ID',
            accountExternalCode: 'External Code',
            isExternalPayment: 'External Payment',
            tokenId: 'Token ID',
            tokenDisplayNumber: 'Token Display Number',
            cardType: 'Card Type',
            rfid: 'RFID',
            energyCostExcVat: 'Energy Fee Excl. VAT',
            energyVat: 'Energy Fee VAT',
            energyCostIncVat: 'Energy Fee Incl. VAT',
            timeFeeWithoutVat: 'Time Fee Excl. VAT',
            timeFeeVat: 'Time Fee VAT',
            timeFee: 'Time Fee Incl. VAT',
            connectionFeeWithoutVat: 'Flat Fee Excl. VAT',
            connectionFeeVat: 'Flat Fee VAT',
            connectionFee: 'Flat Fee Incl. VAT',
            idleFeeWithoutVat: 'Idle Fee Excl. VAT',
            idleFeeVat: 'Idle Fee VAT',
            idleFee: 'Idle Fee Incl. VAT',
            totalPaymentCostExcVat: 'Total Fees Excl. VAT',
            totalPaymentVat: 'Total Fees VAT',
            totalPaymentCostIncVat: 'Total Fees Incl. VAT',
            settlementExcVat: 'Settlement Excl. VAT',
            settlementVat: 'Settlement VAT',
            settlementIncVat: 'Settlement Incl. VAT',
            communityName: 'Discount Type',
            communityValue: 'Discount Info',
            discountExcVat: 'Discount Excl. VAT',
            discountVat: 'Discount VAT',
            discountIncVat: 'Discount Incl. VAT',
            totalPaymentAfterDiscountExcVat: 'Total Cost Excl. VAT',
            totalPaymentAfterDiscountVat: 'Total Cost VAT',
            totalPaymentAfterDiscountIncVat: 'Total Cost Incl. VAT',
            paymentId: 'Nayax Transaction ID',
            utilityEnergyCost: 'Utility Energy Cost Excl. VAT',
            energyIncome: 'Energy Income Excl. VAT',
            totalIncome: 'Total Income Excl. VAT',
            l1UnitCost: 'L1 Unit Cost',
            l1Energy: 'L1 Energy',
            l1Cost: 'L1 Cost',
            l2UnitCost: 'L2 Unit Cost',
            l2Energy: 'L2 Energy',
            l2Cost: 'L2 Cost',
            l3UnitCost: 'L3 Unit Cost',
            l3Energy: 'L3 Energy',
            l3Cost: 'L3 Cost',
            l4UnitCost: 'L4 Unit Cost',
            l4Energy: 'L4 Energy',
            l4Cost: 'L4 Cost',
            l5UnitCost: 'L5 Unit Cost',
            l5Energy: 'L5 Energy',
            l5Cost: 'L5 Cost',
            l6UnitCost: 'L6 Unit Cost',
            l6Energy: 'L6 Energy',
            l6Cost: 'L6 Cost',
            l7UnitCost: 'L7 Unit Cost',
            l7Energy: 'L7 Energy',
            l7Cost: 'L7 Cost',
            l8UnitCost: 'L8 Unit Cost',
            l8Energy: 'L8 Energy',
            l8Cost: 'L8 Cost',
            l9UnitCost: 'L9 Unit Cost',
            l9Energy: 'L9 Energy',
            l9Cost: 'L9 Cost',
            evTierPricing: 'EV Tier Pricing',
            vatRate: 'VAT Rate',
            currencyName: 'Currency',
            isVirtualSession: 'Virtual Session',
            nayaxTransactionId: 'Nayax Transaction ID (Duplicate)',
          };

          // HIVE-6234: Filter columns if selectedColumns is provided

          // Check if fee groups have data (same logic as UI)
          const hasEnergyFeeData = allColumnsData.some(row =>
            (row.energyCostExcVat !== '' && row.energyCostExcVat !== '0.00') ||
            (row.energyVat !== '' && row.energyVat !== '0.00') ||
            (row.energyCostIncVat !== '' && row.energyCostIncVat !== '0.00')
          );
          const hasFlatFeeData = allColumnsData.some(row =>
            (row.connectionFee !== '' && row.connectionFee !== '0.00') ||
            (row.connectionFeeWithoutVat !== '' && row.connectionFeeWithoutVat !== '0.00') ||
            (row.connectionFeeVat !== '' && row.connectionFeeVat !== '0.00')
          );
          const hasTimeFeeData = allColumnsData.some(row =>
            (row.timeFee !== '' && row.timeFee !== '0.00') ||
            (row.timeFeeWithoutVat !== '' && row.timeFeeWithoutVat !== '0.00') ||
            (row.timeFeeVat !== '' && row.timeFeeVat !== '0.00')
          );
          const hasIdleFeeData = allColumnsData.some(row =>
            (row.idleFee !== '' && row.idleFee !== '0.00') ||
            (row.idleFeeWithoutVat !== '' && row.idleFeeWithoutVat !== '0.00') ||
            (row.idleFeeVat !== '' && row.idleFeeVat !== '0.00')
          );
          const hasTotalFeesData = allColumnsData.some(row =>
            (row.totalPaymentCostExcVat !== '' && row.totalPaymentCostExcVat !== '0.00') ||
            (row.totalPaymentVat !== '' && row.totalPaymentVat !== '0.00') ||
            (row.totalPaymentCostIncVat !== '' && row.totalPaymentCostIncVat !== '0.00')
          );
          const hasDiscountData = allColumnsData.some(row =>
            (row.communityName !== '' && row.communityName != null) ||
            (row.communityValue !== '' && row.communityValue != null) ||
            (row.discountExcVat !== '' && row.discountExcVat !== '0.00') ||
            (row.discountVat !== '' && row.discountVat !== '0.00') ||
            (row.discountIncVat !== '' && row.discountIncVat !== '0.00')
          );
          const hasTotalCostData = allColumnsData.some(row =>
            row.totalPaymentAfterDiscountExcVat !== '' ||
            row.totalPaymentAfterDiscountVat !== '' ||
            row.totalPaymentAfterDiscountIncVat !== ''
          );

          // Expand group IDs to individual column IDs
          // Skip groups that have no data (matching UI behavior)
          const expandGroupColumns = (columns: string[]): string[] => {
            const expanded: string[] = [];
            columns.forEach(col => {
              // Check if it's a group ID and expand it (only if it has data)
              if (col === 'energyFeeGroup') {
                if (hasEnergyFeeData) {
                  expanded.push('energyCostExcVat', 'energyVat', 'energyCostIncVat');
                }
              } else if (col === 'timeFeeGroup') {
                if (hasTimeFeeData) {
                  expanded.push('timeFeeWithoutVat', 'timeFeeVat', 'timeFee');
                }
              } else if (col === 'flatFeeGroup') {
                if (hasFlatFeeData) {
                  expanded.push('connectionFeeWithoutVat', 'connectionFeeVat', 'connectionFee');
                }
              } else if (col === 'idleFeeGroup') {
                if (hasIdleFeeData) {
                  expanded.push('idleFeeWithoutVat', 'idleFeeVat', 'idleFee');
                }
              } else if (col === 'totalFeesGroup') {
                if (hasTotalFeesData) {
                  expanded.push('totalPaymentCostExcVat', 'totalPaymentVat', 'totalPaymentCostIncVat');
                }
              } else if (col === 'settlementGroup') {
                // Settlement is always included if selected (it's part of totalFeesGroup check)
                expanded.push('settlementExcVat', 'settlementVat', 'settlementIncVat');
              } else if (col === 'discountGroup') {
                if (hasDiscountData) {
                  expanded.push('communityName', 'communityValue', 'discountExcVat', 'discountVat', 'discountIncVat');
                }
              } else if (col === 'totalCostGroup') {
                if (hasTotalCostData) {
                  expanded.push('totalPaymentAfterDiscountExcVat', 'totalPaymentAfterDiscountVat', 'totalPaymentAfterDiscountIncVat');
                }
              } else if (col.startsWith('tariff-l')) {
                // Tariff group (e.g., 'tariff-l1') -> expand to l1UnitCost, l1Energy, l1Cost
                const levelNum = col.replace('tariff-l', '');
                expanded.push(`l${levelNum}UnitCost`, `l${levelNum}Energy`, `l${levelNum}Cost`);
              } else {
                // Regular column ID
                expanded.push(col);
              }
            });
            return expanded;
          };

          const filteredData = selectedColumns && selectedColumns.length > 0
            ? (() => {
                const expandedColumns = expandGroupColumns(selectedColumns);
                return allColumnsData.map(row => {
                  const filtered: Record<string, any> = {};
                  expandedColumns.forEach(col => {
                    if (col in row) {
                      filtered[col] = row[col as keyof typeof row];
                    }
                  });
                  return filtered;
                });
              })()
            : allColumnsData; // Export all columns if no selection

          // Transform data to use display names as headers
          const exportData = filteredData.map((row: Record<string, any>) => {
            const transformed: Record<string, any> = {};
            Object.keys(row).forEach(key => {
              const displayName = columnHeaders[key] || key;
              transformed[displayName] = row[key];
            });
            return transformed;
          });

          // Create workbook and worksheet
          const worksheet = XLSX.utils.json_to_sheet(exportData);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Sessions');

          // Auto-size columns
          const colWidths = Object.keys(exportData[0] || {}).map((key) => ({
            wch: Math.max(key.length, 15),
          }));
          worksheet['!cols'] = colWidths;

          // Generate filename with date
          const filename = `sessions-export-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.xlsx`;

          // Download
          XLSX.writeFile(workbook, filename);

          set({ isExportingSessions: false, exportProgress: null });
        } catch (error) {
          console.error('Failed to export sessions:', error);
          set({
            error:
              error instanceof Error
                ? error.message
                : 'Failed to export sessions',
            isExportingSessions: false,
            exportProgress: null,
          });
        }
      },

      // ---- Meter Values Actions ----

      /**
       * Open meter values blade
       */
      openMeterValuesBlade: () => {
        set({
          isMeterValueBladeOpen: true,
          meterValues: [],
          meterValuesCount: 0,
          meterValuesPage: 0,
          meterValuesError: null,
        });
        // Load meter values when blade opens
        get().loadMeterValues();
      },

      /**
       * Close meter values blade
       */
      closeMeterValuesBlade: () => {
        set({
          isMeterValueBladeOpen: false,
          meterValues: [],
          meterValuesCount: 0,
          meterValuesPage: 0,
          meterValuesError: null,
        });
      },

      /**
       * Load meter values for the selected session
       */
      loadMeterValues: async () => {
        const { selectedSession, meterValuesPage, meterValuesPageSize, filterEmptyDelta } = get();

        if (!selectedSession) {
          set({ meterValuesError: 'No session selected' });
          return;
        }

        set({ isLoadingMeterValues: true, meterValuesError: null });

        try {
          const services = servicesContainer.getServices();

          const result = await services.getMeterValuesUseCase.execute(
            selectedSession.sessionId,
            meterValuesPage,
            meterValuesPageSize,
            'timeStamp desc',
            filterEmptyDelta
          );

          set({
            meterValues: result.meterValues,
            meterValuesCount: result.totalCount,
            isLoadingMeterValues: false,
          });
        } catch (error) {
          console.error('Failed to load meter values:', error);
          set({
            meterValuesError:
              error instanceof Error
                ? error.message
                : 'Failed to load meter values',
            isLoadingMeterValues: false,
          });
        }
      },

      /**
       * Load a specific page of meter values
       */
      loadMeterValuesPage: async (page: number) => {
        set({ meterValuesPage: page });
        await get().loadMeterValues();
      },

      /**
       * Change meter values page size and reload
       */
      setMeterValuesPageSize: (size: number) => {
        set({ meterValuesPageSize: size, meterValuesPage: 0 });
        get().loadMeterValues();
      },

      /**
       * Toggle filter to hide rows with Delta Energy = 0
       */
      setFilterEmptyDelta: (value: boolean) => {
        set({ filterEmptyDelta: value, meterValuesPage: 0 });
        get().loadMeterValues();
      },

      /**
       * Export meter values to Excel
       */
      exportMeterValues: async () => {
        const { selectedSession, meterValuesCount, enabledColumnGroups } = get();

        if (!selectedSession) {
          return;
        }

        set({ isExportingMeterValues: true });

        // Derive cost/VAT visibility flags from column groups (matching Flutter downloadFile logic)
        const showCost = enabledColumnGroups.includes('cost');
        const showBuyInfo = enabledColumnGroups.includes('buyInfo');
        const showVat = enabledColumnGroups.includes('vatInclude');

        try {
          const services = servicesContainer.getServices();
          await services.getMeterValuesUseCase.exportToExcel(
            selectedSession.sessionId,
            meterValuesCount || 10000, // Fallback to large number if count not available
            showVat,
            showCost || showBuyInfo,  // addCost: true when either sell or buy cost columns are shown
            showBuyInfo,              // addBuyCost
            showCost,                 // addSellCost
          );
          set({ isExportingMeterValues: false });
        } catch (error) {
          console.error('Failed to export meter values:', error);
          set({
            meterValuesError:
              error instanceof Error
                ? error.message
                : 'Failed to export meter values',
            isExportingMeterValues: false,
          });
        }
      },
    }),
    {
      name: 'sessions-report-storage',
      // Persist filters, page size, and column groups (HIVE-6245 #11)
      // HIVE-6307: Exclude liveTransactions from persistence (default to false)
      partialize: (state) => ({
        filters: {
          ...state.filters,
          liveTransactions: false,  // HIVE-6307: Never persist, always default false
          last4Digits: undefined,   // HIVE-6307: Don't persist last4Digits filter
        },
        pageSize: state.pageSize,
        enabledColumnGroups: state.enabledColumnGroups,
        filterEmptyDelta: state.filterEmptyDelta, // Persist user's choice for meter values filter
      }),
    }
  )
);
